// @ts-check
import { FileText, RefreshCw, ShieldCheck, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import atelierAdminService from '../services/atelierAdminService';

const STATUSES = ['all', 'pending', 'approved', 'rejected', 'suspended'];
const STATUS_STYLES = {
  pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
  approved: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
  rejected: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  suspended: 'bg-accent text-foreground/80',
};

export default function AtelierAccountsPage() {
  const toast = useToast();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await atelierAdminService.listAccounts(filter === 'all' ? null : filter);
      setAccounts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load trade accounts');
    } finally {
      setLoading(false);
    }
  }, [filter, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Trade Accounts</h1>
          <p className="text-sm text-muted-foreground">B2B buyers — review, verify, and approve.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-accent" title="Refresh">
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-sm rounded-full capitalize ${
              filter === s ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground/80 hover:bg-accent'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="bg-card border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Country</th>
              <th className="px-4 py-3">IEC</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Registered</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} className="border-t">
                <td className="px-4 py-3 font-medium">{a.company_name}</td>
                <td className="px-4 py-3">
                  {a.contact_person_name}
                  <br />
                  <span className="text-muted-foreground/70">{a.business_email}</span>
                </td>
                <td className="px-4 py-3">{a.country}</td>
                <td className="px-4 py-3">{a.iec_number || '—'}</td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs capitalize ${STATUS_STYLES[a.status] || 'bg-muted'}`}
                  >
                    {a.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setSelected(a)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Manage
                  </button>
                </td>
              </tr>
            ))}
            {!loading && accounts.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground/70">
                  No accounts
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && <AccountModal account={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}

function AccountModal({ account, onClose, onChanged }) {
  const toast = useToast();
  const [docs, setDocs] = useState([]);
  const [nda, setNda] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectionPresets, setRejectionPresets] = useState([]);
  const [notesDirty, setNotesDirty] = useState(false);

  // The X button dismisses the modal — confirm first if the notes field
  // below has unsaved input.
  const handleCloseClick = () => {
    if (notesDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const refresh = useCallback(async () => {
    try {
      const [d, n] = await Promise.all([
        atelierAdminService.getAccountDocuments(account.id),
        atelierAdminService.getNda(account.id).catch(() => null),
      ]);
      setDocs(Array.isArray(d) ? d : []);
      setNda(n);
    } catch (e) {
      toast.error(e.message || 'Failed to load account details');
    }
  }, [account.id, toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    atelierAdminService
      .listAccountRejectionReasons()
      .then((data) => setRejectionPresets((data || []).filter((r) => r.is_active)))
      .catch(() => {});
  }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await refresh();
      onChanged();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      maxWidth="2xl"
      onClose={handleCloseClick}
      className="rounded-lg"
      closeOnBackdrop={false}
      hasUnsavedChanges={notesDirty}
    >
      <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-card">
        <h2 className="text-lg font-semibold">{account.company_name}</h2>
        <button onClick={handleCloseClick}>
          <X size={20} />
        </button>
      </div>

      <div className="p-5 space-y-5 text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Contact" value={account.contact_person_name} />
          <Field label="Email" value={account.business_email} />
          <Field label="Country" value={account.country} />
          <Field label="Business type" value={account.business_type} />
          <Field label="IEC" value={account.iec_number} />
          <Field label="Tax ID" value={account.tax_id} />
          <Field label="Reg. no." value={account.registration_number} />
          <Field label="Website" value={account.website} />
        </div>

        <div>
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <FileText size={16} /> Verification documents
          </h3>
          {docs.length === 0 ? (
            <p className="text-muted-foreground/70">No documents uploaded.</p>
          ) : (
            <ul className="space-y-1">
              {docs.map((d) => (
                <li key={d.id} className="flex justify-between border rounded-lg px-3 py-2">
                  <span className="capitalize">{d.doc_type.replace(/_/g, ' ')}</span>
                  <a
                    href={d.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    View
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <ShieldCheck size={16} /> Mutual NDA
          </h3>
          <p className="text-muted-foreground mb-2">
            Status: <span className="font-medium">{nda ? nda.status : 'not issued'}</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              disabled={busy}
              onClick={() => act(() => atelierAdminService.generateNda(account.id), 'NDA generated & sent')}
              className="btn-sm bg-primary text-primary-foreground"
            >
              Generate & send NDA
            </button>
            {nda?.signed_document_url && (
              <a href={nda.signed_document_url} target="_blank" rel="noreferrer" className="btn-sm bg-muted">
                View signed copy
              </a>
            )}
            <button
              disabled={busy || !nda?.signed_document_url}
              onClick={() => act(() => atelierAdminService.markNdaSigned(account.id), 'NDA marked signed')}
              className="btn-sm bg-green-600 text-white disabled:opacity-40"
            >
              Mark signed
            </button>
          </div>
        </div>

        <div className="border-t pt-4">
          <AccountNotes accountId={account.id} onDirtyChange={setNotesDirty} />
        </div>

        <div className="border-t pt-4">
          <h3 className="font-medium mb-2">Approval</h3>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              disabled={busy}
              onClick={() => act(() => atelierAdminService.approveAccount(account.id), 'Account approved')}
              className="btn-sm bg-green-600 text-white"
            >
              Approve
            </button>
            <button
              disabled={busy}
              onClick={() => act(() => atelierAdminService.suspendAccount(account.id), 'Account suspended')}
              className="btn-sm bg-accent"
            >
              Suspend
            </button>
          </div>
          <div className="flex flex-col gap-2 mt-3">
            {rejectionPresets.length > 0 && (
              <select
                value=""
                onChange={(e) => e.target.value && setRejectReason(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm text-muted-foreground"
              >
                <option value="">Pick a preset reason…</option>
                {rejectionPresets.map((r) => (
                  <option key={r.id} value={r.label}>
                    {r.label}
                  </option>
                ))}
              </select>
            )}
            <div className="flex gap-2">
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Rejection reason (required)"
                className="flex-1 border rounded-lg px-3 py-1.5"
              />
              <button
                disabled={busy || !rejectReason.trim()}
                onClick={() =>
                  act(() => atelierAdminService.rejectAccount(account.id, rejectReason.trim()), 'Account rejected')
                }
                className="btn-sm bg-red-600 text-white disabled:opacity-40"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`.btn-sm{padding:.4rem .8rem;border-radius:.375rem;font-size:.8rem}`}</style>
    </Modal>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-muted-foreground/70 text-xs">{label}</div>
      <div>{value || '—'}</div>
    </div>
  );
}

function AccountNotes({ accountId, onDirtyChange }) {
  const toast = useToast();
  const [notes, setNotes] = useState('');
  useEffect(() => {
    atelierAdminService
      .getAccountNotes(accountId)
      .then((d) => setNotes(d?.notes || ''))
      .catch(() => {});
  }, [accountId]);
  const save = async () => {
    try {
      await atelierAdminService.setAccountNotes(accountId, notes);
      toast.success('Notes saved');
      onDirtyChange?.(false);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };
  return (
    <div>
      <h3 className="font-medium mb-2">
        Internal notes <span className="text-xs text-muted-foreground/70">(not visible to buyer)</span>
      </h3>
      <textarea
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          onDirtyChange?.(true);
        }}
        rows={3}
        className="w-full border rounded-lg px-3 py-2 mb-2"
      />
      <button onClick={save} className="btn-sm bg-primary text-primary-foreground">
        Save notes
      </button>
    </div>
  );
}
